/**
 * Royal Lady — DPDP consent store (P1-13)
 *
 * Client-side authority on the user's consent choice. Persists to
 * localStorage and mirrors to the backend for logged-in users so the
 * backend can enforce the choice server-side for analytics writes.
 *
 * DPDP §6/§7 principles honoured:
 *  • Free, specific, informed, unambiguous consent (banner + granular toggles)
 *  • Purpose limitation (essential / analytics / marketing kept separate)
 *  • Right to withdraw as easily as given (Cookie preferences link)
 *  • Essential shopping continues to work regardless of optional consent
 *
 * Storage shape:
 *   { v: 1, essential: true, analytics: bool, marketing: bool,
 *     decidedAt: <iso>, source: "banner" | "customize" | "footer" }
 */

const STORAGE_KEY = "rl_consent_v1";
export const CONSENT_VERSION = 1;

const DEFAULT_UNSET = {
  v: CONSENT_VERSION,
  essential: true,
  analytics: null,   // null = not decided yet
  marketing: null,
  decidedAt: null,
  source: null,
};

const DEFAULT_ACCEPT_ALL = {
  v: CONSENT_VERSION,
  essential: true,
  analytics: true,
  marketing: true,
  decidedAt: new Date().toISOString(),
  source: "banner",
};

const DEFAULT_REJECT_OPTIONAL = {
  v: CONSENT_VERSION,
  essential: true,
  analytics: false,
  marketing: false,
  decidedAt: new Date().toISOString(),
  source: "banner",
};

const listeners = new Set();

function _read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_UNSET };
    const parsed = JSON.parse(raw);
    // If version bumped in future, treat old consent as re-prompt trigger.
    if (parsed?.v !== CONSENT_VERSION) return { ...DEFAULT_UNSET };
    return {
      v: CONSENT_VERSION,
      essential: true, // essential is always true
      analytics: parsed.analytics ?? null,
      marketing: parsed.marketing ?? null,
      decidedAt: parsed.decidedAt ?? null,
      source: parsed.source ?? null,
    };
  } catch (_) {
    return { ...DEFAULT_UNSET };
  }
}

function _write(next) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (_) { /* ignore quota / private-mode failures */ }
  listeners.forEach((fn) => {
    try { fn(next); } catch (_) { /* ignore */ }
  });
}

export function getConsent() {
  return _read();
}

export function hasDecidedConsent() {
  const c = _read();
  return c.analytics !== null || c.marketing !== null;
}

export function setConsent(nextPartial, { source = "customize" } = {}) {
  const current = _read();
  const next = {
    ...current,
    ...nextPartial,
    v: CONSENT_VERSION,
    essential: true,
    decidedAt: new Date().toISOString(),
    source,
  };
  _write(next);
  // Mirror to backend (fire-and-forget). Never blocks / throws.
  try {
    const token = localStorage.getItem("royal_lady_token");
    const url = (process.env.REACT_APP_BACKEND_URL || "") + "/api/consent";
    const body = {
      analytics: !!next.analytics,
      marketing: !!next.marketing,
      source,
    };
    // Use fetch keepalive so this survives navigation.
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      credentials: "include",
      keepalive: true,
    }).catch(() => { /* silently ignore */ });
  } catch (_) { /* ignore */ }
  return next;
}

export function acceptAll() {
  return setConsent(
    { analytics: true, marketing: true },
    { source: "banner" },
  );
}

export function rejectOptional() {
  return setConsent(
    { analytics: false, marketing: false },
    { source: "banner" },
  );
}

export function subscribeConsent(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ---------------- Header contract with backend ----------------

/**
 * Header value the frontend adds to every API call so the backend can enforce
 * the user's analytics choice server-side. "0" = declined, "1" = granted,
 * "-" = not decided (server default is "1" — record events but keep them
 * anonymous, no PII).
 */
export function analyticsConsentHeader() {
  const c = _read();
  if (c.analytics === true) return "1";
  if (c.analytics === false) return "0";
  return "-";
}
